import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  useCheckout,
  useRsvp,
  useMsci,
  useBayonet,
  usePromotions,
  useAddOns,
  useResetStore,
  usePaths,
  useCustomLinksActions,
  useExchange,
} from '@/store';
import { useLazyGetEventsByIdOrNameQuery } from './event.api';
import { setEvent as setCurrentEvent } from './event.slice';
import { setAllTickets as setAllT } from '../ticket-types/ticket-type.slice';
import useBoletiaConfig, { Templates } from '@/hooks/useBoletiaConfig';
import useAddOnsConfig from '@/hooks/useAddOnsConfig';
import { useEmbedded } from '@/store';
import { isEmbeddedInWallet, getAppToken } from '@/utils/web-view';

import {
  getZone,
  getData,
} from '@/store/tickets-zone-capacity/mockMaxCapacity';

const formatResponse = (data) => {
  const formatedResponse = {};

  const {
    event: { id: eventID, user_id: userID },
  } = data.event_information;
  const maxCapacity = getData(userID, eventID);
  if (maxCapacity) {
    formatedResponse.event_information = {
      ...data.event_information,
      event: {
        ...data.event_information.event,
        real_capacity: maxCapacity.maxCapacity,
      },
    };
    formatedResponse.ticket_types_information =
      data.ticket_types_information.map((t) => ({
        id: t.ticket_type_id,
        ...t,
        zone: getZone(userID, eventID, t.name),
      }));
  } else {
    return data;
  }

  return formatedResponse;
};

const useEvent = ({ subdomain, link, skipRToken }) => {
  const dispatch = useDispatch();
  const { setSubdomain } = useCheckout();
  const [trigger] = useLazyGetEventsByIdOrNameQuery();
  const { isEventWithoutTicketInsurance } = useAddOnsConfig();
  const { getTemplate, getPromotionPreLoaded, isEventsWithAddOns } =
    useBoletiaConfig();
  const { activeAddOns, disableTicketInsurance } = useAddOns();
  const { loadRsvp } = useRsvp();
  const { load: loadMsci } = useMsci();
  const { load: loadBayonet } = useBayonet();
  const { getPromotion } = usePromotions();
  const { disableStep } = usePaths();
  const { restore } = useResetStore();
  const { setCustomLinkData } = useCustomLinksActions();
  const { setSkipRToken } = useExchange();

  const { loadEmbeddedData } = useEmbedded();

  const [isLoading, setIsLoading] = useState(true);
  const [event, setEvent] = useState();
  const [error, setError] = useState();
  const [template, setTemplate] = useState(Templates.PLUS);

  const load = async () => {
    setSubdomain(subdomain);
    try {
      setIsLoading(true);
      const { data, error } = await trigger({ subdomain, link }, false);
      setIsLoading(false);
      if (data) {
        const formatedData = formatResponse(data);
        setData(formatedData);

        if (link && data.event_information.event.link.use) {
          setCustomLinkData({ ...data.event_information.event.link, link });
        }

        return {
          event: formatedData,
          error,
          template: getTemplate(
            formatedData.event_information.event.id,
            formatedData.event_information.event.user_id,
          ),
        };
      } else {
        return { event: null, error, template: null };
      }
    } catch {}
    return { event: null, error: true, template: null };
  };

  const setData = (data) => {
    if (data) {
      dispatch(
        setCurrentEvent({
          ...data.event_information,
          hasEventAvailability: data?.ticket_types_information?.some(
            (ticket) => ticket.availability > 0,
          ),
          gateway: data?.payment_methods.filter(
            (method) => method.type === 'Gateway::Banwire',
          )[0]?.payment_gateway,
        }),
      );
      if (data.ticket_types_information) {
        dispatch(
          setAllT(
            data.ticket_types_information.map((t) => ({
              id: t.ticket_type_id,
              ...t,
            })),
          ),
        );
      }
    }
  };

  const loadEventData = async (subdomain) => {
    const { event: ev, error: er, template: tmlp } = await load(subdomain);
    if (ev) {
      // loads data to know if app is embedded and takes auth token
      loadEmbeddedData({
        isEmbedded: isEmbeddedInWallet(), // isInWebView() && source === 'wallet',
        data: {
          appToken: getAppToken(),
        },
      });
      await loadMsci(ev.event_information.event.id);
      await loadRsvp(ev.event_information.event.id);
      await loadBayonet();
      setSkipRToken(skipRToken);
      setEvent(ev);
      const promotion = getPromotionPreLoaded(ev.event_information.event.id);
      if (promotion) {
        await getPromotion(promotion, ev.event_information.event.id);
      }

      if (
        isEventWithoutTicketInsurance(ev.event_information?.event?.id) ||
        ev.event_information?.preferences?.currency !== 'MXN'
      ) {
        disableTicketInsurance();
        disableStep('ADDONS');
        activeAddOns(false);
      }
    }
    setTemplate(tmlp);
    setError(er);
  };

  useEffect(() => {
    restore();
    loadEventData();
  }, []);

  return {
    isLoading,
    event,
    error,
    template,
  };
};

export default useEvent;
